/**
 * components/client-detail/ClientCaseSelector.tsx
 *
 * Floating dropdown for selecting the active dossier when a client has 2+ cases.
 * Positioned between the header card and the content card.
 *
 * Trigger: compact button — folder icon + active case name + chevron (no counters).
 * Dropdown panel (portal):
 *   - Optional search input (from 5+ cases)
 *   - List of cases: name only + per-row rename/delete kebab on hover
 *   - Active case marked with a checkmark
 *   - No "new case" footer (case creation is handled by the normal workflow)
 *
 * Returns null when the client has only one case.
 *
 * Created Apr 2026 — part of ClientDetail refonte.
 * Updated Apr 2026 — removed counters, removed "new case" footer, removed external
 *   "Dossier" label; trigger is self-labelled.
 * Updated Apr 2026 — saveRename only closes editor on success (try/finally); row
 *   wrapper changed from <button> to <div role="button"> to fix interactive-inside-button
 *   HTML violation; early return guard moved after hooks to keep hook order stable.
 */

import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  FolderOpen, ChevronDown, Check, MoreHorizontal,
  Pencil, Trash2, Search, Loader2, X,
} from 'lucide-react';
import { Z_INDEX } from '../../constants/zIndex';
import type { ClientCase } from '../../hooks/useClientCases';

interface ClientCaseSelectorProps {
  cases: ClientCase[];
  activeCaseId: string | null;
  onSelectCase: (caseId: string) => void;
  onRenameCase: (caseId: string, name: string) => Promise<boolean>;
  onDeleteCase: (caseId: string) => Promise<boolean>;
}

interface RowMenuProps {
  c: ClientCase;
  isDeletable: boolean;
  onRename: (id: string, name: string) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
  onClosePanel: () => void;
}

function RowMenu({ c, isDeletable, onRename, onDelete, onClosePanel }: RowMenuProps) {
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [saving, setSaving] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (renaming && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [renaming]);

  const startRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    setRenameValue(c.name);
    setRenaming(true);
  };

  const saveRename = async () => {
    const name = renameValue.trim();
    if (!name) { setRenaming(false); return; }
    setSaving(true);
    let ok = false;
    try {
      ok = await onRename(c.id, name);
    } finally {
      setSaving(false);
    }
    if (ok) setRenaming(false);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    onClosePanel();
    await onDelete(c.id);
  };

  if (renaming) {
    return (
      <div className="flex items-center gap-1.5 ml-auto" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); saveRename(); }
            if (e.key === 'Escape') { e.preventDefault(); setRenaming(false); }
            e.stopPropagation();
          }}
          className="text-sm border border-neutral-300 rounded-lg px-2 py-0.5 outline-none focus:ring-2 focus:ring-primary-600 w-32"
        />
        {saving
          ? <Loader2 className="w-3.5 h-3.5 animate-spin text-neutral-400" />
          : <button onClick={saveRename} className="text-primary-600 hover:text-primary-800 transition-colors"><Check className="w-3.5 h-3.5" /></button>
        }
        <button onClick={() => setRenaming(false)} className="text-neutral-400 hover:text-neutral-600 transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={menuRef} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className={`opacity-0 group-hover/row:opacity-100 transition-opacity p-1 rounded text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 focus:outline-none focus-visible:opacity-100 ${open ? 'opacity-100' : ''}`}
      >
        <MoreHorizontal className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-40 bg-white border border-neutral-200 rounded-xl shadow-soft-lg py-1 overflow-hidden"
          style={{ zIndex: Z_INDEX.POPOVER + 1 }}
        >
          <button
            onClick={startRename}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            <Pencil className="w-3.5 h-3.5 text-neutral-400" />
            Renommer
          </button>
          {isDeletable && (
            <button
              onClick={handleDelete}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-error-600 hover:bg-error-50 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Supprimer
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function ClientCaseSelector({
  cases,
  activeCaseId,
  onSelectCase,
  onRenameCase,
  onDeleteCase,
}: ClientCaseSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });

  const showSelector = cases.length >= 2;

  useLayoutEffect(() => {
    if (!showSelector || !open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelPos({
      top: rect.bottom + window.scrollY + 6,
      left: rect.left + window.scrollX,
      width: Math.max(rect.width, 240),
    });
  }, [showSelector, open]);

  useEffect(() => {
    if (!showSelector || !open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSelector, open]);

  if (!showSelector) return null;

  const activeCase = cases.find((c) => c.id === activeCaseId);
  const showSearch = cases.length >= 5;
  const filtered = cases.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

  const handleSelect = (id: string) => {
    onSelectCase(id);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="flex items-center">
      <button
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3.5 py-2 bg-white border border-neutral-200 rounded-xl text-sm text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50 transition-all shadow-soft focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:outline-none max-w-xs"
      >
        <FolderOpen className="w-4 h-4 text-neutral-400 flex-shrink-0" />
        <span className="truncate font-medium">{activeCase?.name ?? '—'}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-neutral-400 flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'absolute', top: panelPos.top, left: panelPos.left, width: panelPos.width, zIndex: Z_INDEX.POPOVER }}
          className="bg-white border border-neutral-200 rounded-2xl shadow-soft-xl overflow-hidden"
        >
          {showSearch && (
            <div className="px-3 pt-3 pb-2">
              <div className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-xl px-3 py-2">
                <Search className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Rechercher…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-neutral-700 placeholder-neutral-400 outline-none"
                  autoFocus
                />
              </div>
            </div>
          )}

          <ul className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-4 py-3 text-sm text-neutral-400 text-center">Aucun résultat</li>
            )}
            {filtered.map((c) => {
              const isActive = c.id === activeCaseId;
              return (
                <li key={c.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelect(c.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(c.id); } }}
                    className="group/row w-full flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 transition-colors cursor-pointer focus:outline-none focus-visible:bg-neutral-50"
                  >
                    <FolderOpen className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-neutral-400'}`} />
                    <span className={`flex-1 text-sm truncate ${isActive ? 'font-semibold text-neutral-900' : 'font-medium text-neutral-700'}`}>
                      {c.name}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isActive && <Check className="w-3.5 h-3.5 text-primary-600" />}
                      <RowMenu
                        c={c}
                        isDeletable={!isActive}
                        onRename={onRenameCase}
                        onDelete={onDeleteCase}
                        onClosePanel={() => setOpen(false)}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
}
